/**
 * Transfer of a car to another firma of the group — hooks for the "Převést do firmy…" dialog
 * (skupina-vlastnictvi-plan S2). One endpoint per stock; the body is the same.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { pujcovnaKeys } from '@/modules/pujcovna/api';
import { useModuleAccess } from '@/lib/permissions';
import { useDebouncedValue } from '@/lib/use-debounced-value';

export type StockKind = 'nove' | 'ojete';

export interface TransferBody {
  id_firmy_cil: number;
  /** Receiving firma's own purchase price (the intra-group invoice amount). */
  nakupni_cena: string;
  nakup_platce_dph: 0 | 1;
  poznamka?: string;
  /** Which stock the car lands in; a NEW car may stay a new car. Default (server): used. */
  cil_typ?: StockKind;
}

export interface TransferResult {
  id_vozidla: number;
  id_firmy_cil: number;
  /** A firm customer was matched by IČO in the target firma ('prevzat') or copied there ('vytvoren'); null = none carried. */
  zakaznik: 'prevzat' | 'vytvoren' | null;
  prevedeno_radku: number;
  /** `prevod` = a live car moved; `zpetne_*` = the retroactive variant (row created / the firma's own row linked). */
  rezim: 'prevod' | 'zpetne_vytvoreno' | 'zpetne_propojeno';
  /** The stock `id_vozidla` is in. */
  typ_cile: StockKind;
}

/** A handed-over car whose buyer is a firma of the group and whose transfer is not filled in yet. */
export interface ProdanoSkupineRow {
  id_vozidla: number;
  vin: string;
  spz: string;
  znacka_nazev: string | null;
  model_nazev: string | null;
  provedeni: string;
  datum_predani: string | null;
  zakaznik_jmeno: string;
  kupujici_firmy: { id_firmy: number; Nazev: string }[];
  /** What the retroactive transfer would do: create the row in the buyer firma, link the one it holds, or be refused. */
  akce: 'vytvorit' | 'propojit' | 'blokovano';
  v_cili_id_vozidla: number | null;
  v_cili_id_firmy: number | null;
  v_cili_typ: StockKind | null;
  duvod: string | null;
  /** Total of the latest closed sale contract — a suggested purchase price. */
  navrhovana_cena: string | null;
}

const PATH: Record<StockKind, string> = { nove: '/v1/vehicles', ojete: '/v1/ojeta-vozidla' };

/** One transfer call (the dialog's mutation and the backlog's bulk action both use it). */
export const postTransfer = (kind: StockKind, id: number, body: TransferBody) =>
  api.post<TransferResult>(`${PATH[kind]}/${id}/transfer`, body);

export function useTransferVozidloMutation(kind: StockKind, id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransferBody) => postTransfer(kind, id, body),
    onSuccess: () => {
      // The source row changed state, and the used-car stock of the other firma changed too.
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
      void qc.invalidateQueries({ queryKey: ['ojeta'] });
      void qc.invalidateQueries({ queryKey: ['skupiny', 'prodano'] });
      void qc.invalidateQueries({ queryKey: ['servis', 'vehicle-search'] });
      void qc.invalidateQueries({ queryKey: ['uskladneni', 'vehicle-search'] });
      void qc.invalidateQueries({ queryKey: ['skupiny', 'vin'] });
      // The rental fleet entry is repointed to the new owner (stock-exit hook).
      void qc.invalidateQueries({ queryKey: pujcovnaKeys.vozidla });
    },
  });
}

/** The sold-to-the-group backlog of one stock (empty list while `enabled` is false). */
export function useInvalidateAfterTransfer() {
  const qc = useQueryClient();
  return () => {
    for (const key of [['vehicles'], ['ojeta'], ['skupiny', 'prodano'], ['servis', 'vehicle-search'], ['uskladneni', 'vehicle-search'], ['skupiny', 'vin'], pujcovnaKeys.vozidla]) {
      void qc.invalidateQueries({ queryKey: key });
    }
  };
}

/** The suggested purchase price of a transfer, WITHOUT VAT (`GET …/{id}/cena-prevodu`): the sale contract, else the car's prodejní cena. */
export interface CenaPrevodu {
  cena_bez_dph: string | null;
  zdroj: 'smlouva' | 'prodejni_cena' | null;
}

/** Fetched when the transfer dialog opens (never cached: the price follows the car's contract and price). */
export function useCenaPrevoduQuery(kind: StockKind, id: number, enabled: boolean) {
  return useQuery({
    queryKey: ['skupiny', 'cena-prevodu', kind, id],
    queryFn: ({ signal }) => api.get<CenaPrevodu>(`${PATH[kind]}/${id}/cena-prevodu`, { signal }),
    enabled,
    staleTime: 0,
  });
}

export function useProdanoSkupineQuery(kind: StockKind, enabled = true) {
  return useQuery({
    queryKey: ['skupiny', 'prodano', kind],
    queryFn: ({ signal }) => api.get<{ vozidla: ProdanoSkupineRow[] }>(`${PATH[kind]}/prodano-skupine`, { signal }),
    enabled,
    select: (data) => data.vozidla,
  });
}

/** Another firma of the group holding a live car with a VIN (`GET /v1/vin-skupina/drzitele`). */
export interface VinHolder {
  id_firmy: number;
  firma_nazev: string;
  kind: StockKind;
}

/** Shorter text is not worth a lookup — the server matches whole VINs only. */
const MIN_VIN_CHARS = 6;

const NO_HOLDERS: VinHolder[] = [];

/** Whitespace-free, upper-case — the server's `vin_norm` comparison, so "wba 1" → "WBA1". */
const vinKey = (v: string) => v.replace(/\s/g, '').toUpperCase();

/**
 * Z2b: the other group firmas that already hold a live car with the VIN being typed (advisory, never a
 * block). `ref.id` = the stock row being edited (omit on create), so the firma it was transferred to is
 * not reported. `ref.saved` = the VIN as stored: while the field still holds it nothing was edited, so
 * there is nothing to warn about. Idle for users without a group; empty until the typed VIN has settled
 * and been checked. `ref.disabled` switches the check off (the endpoint always answers for the ACTIVE
 * firma, so it can't judge a row of another firma).
 */
export function useVinSkupina(
  vin: string,
  ref?: { kind: StockKind; id?: number | null; saved?: string | null; disabled?: boolean },
): VinHolder[] {
  const { skupinaFirmy } = useModuleAccess();
  const typed = vin.trim();
  const settled = useDebouncedValue(typed, 400);
  const id = ref?.id ?? null;
  const edited = vinKey(settled) !== vinKey(ref?.saved ?? '');
  const query = useQuery({
    queryKey: ['skupiny', 'vin', settled, ref?.kind ?? null, id],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ vin: settled });
      if (ref && id != null) {
        qs.set('kind', ref.kind);
        qs.set('id', String(id));
      }
      return api.get<{ drzitele: VinHolder[] }>(`/v1/vin-skupina/drzitele?${qs}`, { signal });
    },
    enabled: !ref?.disabled && skupinaFirmy.length > 0 && edited && settled.length >= MIN_VIN_CHARS,
    select: (data) => data.drzitele,
  });
  // While the field has moved on from the checked VIN, the answer is about another text.
  return !ref?.disabled && edited && settled === typed && query.data ? query.data : NO_HOLDERS;
}
